import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DesktopLayout } from '@/components/desktop/DesktopLayout';
import { useSellerSignupData } from '@/hooks/useSellerSignupData';
import { Toast } from '@/components/common/Toast';

// Desktop components following SRP
import { SellerSignupHeader } from '@/components/desktop/auth/seller/SellerSignupHeader';
import { SellerAccountForm } from '@/components/desktop/auth/seller/SellerAccountForm';

interface FormData {
  email: string;
  phone: string;
  name: string;
}

interface PasswordData {
  password: string;
  confirmPassword: string;
}

export function DesktopSellerSignupPage() {
  const navigate = useNavigate();
  const { accountInfo, updateAccountInfo } = useSellerSignupData();
  
  // localStorage에서 복원되지 않는 보안 관련 state (비밀번호)
  const [passwordData, setPasswordData] = useState<PasswordData>({
    password: '',
    confirmPassword: ''
  });
  
  // localStorage에서 복원된 데이터로 초기화
  const [formData, setFormData] = useState<FormData>({
    email: accountInfo.email || '',
    phone: accountInfo.phone || '',
    name: accountInfo.name || ''
  });

  const [isEmailVerified, setIsEmailVerified] = useState(accountInfo.isCodeVerified || false);
  const [_verificationToken, setVerificationToken] = useState('');
  const [toast, setToast] = useState<{ show: boolean; message: string }>({ show: false, message: '' });

  const handleFormChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // localStorage에 저장
    updateAccountInfo({ [field]: value });
  };

  const handlePasswordChange = (field: keyof PasswordData, value: string) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEmailVerified = (verified: boolean, token?: string) => {
    setIsEmailVerified(verified);
    if (token) {
      setVerificationToken(token);
    }
    updateAccountInfo({ isCodeVerified: verified });
  };

  const handleError = (message: string) => {
    setToast({ show: true, message });
  };

  const handleSuccess = (message: string) => {
    setToast({ show: true, message });
  };

  const isFormValid = () => {
    return formData.email && 
           passwordData.password && 
           passwordData.confirmPassword && 
           formData.phone && 
           formData.name && 
           passwordData.password === passwordData.confirmPassword &&
           passwordData.password.length >= 8 &&
           isEmailVerified;
  };

  const handleNext = () => {
    if (!isFormValid()) {
      if (!isEmailVerified) {
        setToast({ show: true, message: '이메일 인증을 완료해주세요' });
      } else if (passwordData.password !== passwordData.confirmPassword) {
        setToast({ show: true, message: '비밀번호가 일치하지 않습니다' });
      } else if (passwordData.password.length < 8) {
        setToast({ show: true, message: '비밀번호는 8자리 이상이어야 합니다' });
      } else {
        setToast({ show: true, message: '모든 필드를 입력해주세요' });
      }
      return;
    }

    // 비밀번호를 sessionStorage에 임시 저장 (회원가입 완료 후 삭제됨)
    sessionStorage.setItem('temp_signup_password', passwordData.password);

    // 다음 단계로 이동 - 사업자 정보 입력
    navigate('/signup/seller/business');
  };

  return (
    <DesktopLayout>
      <div className="min-h-[80vh] flex items-center justify-center py-12">
        <div className="w-full max-w-4xl bg-white rounded-2xl shadow-lg border border-gray-200 px-12 py-16">
          
          {/* Header Section */}
          <SellerSignupHeader 
            title="판매자 회원가입"
            subtitle="에코딜과 함께 음식물 쓰레기를 줄여보세요"
            currentStep={1}
            totalSteps={3}
            className="mb-12" 
          />
          
          {/* Form Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* 좌측: 안내 정보 */}
            <div className="space-y-6">
              <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                <h3 className="text-[20px] font-semibold text-green-700 mb-4">
                  판매자 혜택
                </h3>
                <ul className="space-y-2 text-[16px] text-green-600">
                  <li>• 음식물 쓰레기 처리 비용 절감</li>
                  <li>• 추가 수익 창출 기회</li>
                  <li>• 환경 보호에 기여</li>
                  <li>• 고객과의 직접 소통</li>
                </ul>
              </div>
              
              <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                <h3 className="text-[20px] font-semibold text-blue-700 mb-4">
                  가입 후 절차
                </h3>
                <ol className="space-y-2 text-[16px] text-blue-600">
                  <li>1. 사업자 정보 등록</li>
                  <li>2. 매장 정보 입력</li>
                  <li>3. 심사 및 승인 (1-2영업일)</li>
                  <li>4. 서비스 이용 시작</li>
                </ol>
              </div>
            </div>
            
            {/* 우측: 입력 폼 */}
            <div>
              <SellerAccountForm
                formData={formData}
                passwordData={passwordData}
                onFormChange={handleFormChange}
                onPasswordChange={handlePasswordChange}
                isEmailVerified={isEmailVerified}
                onEmailVerified={handleEmailVerified}
                onError={handleError}
                onSuccess={handleSuccess}
              />
              
              {/* 다음 단계 버튼 */}
              <div className="mt-8">
                <button
                  onClick={handleNext}
                  disabled={!isFormValid()}
                  className={`w-full h-[56px] font-semibold text-[20px] rounded-xl transition-colors ${
                    isFormValid() 
                      ? 'bg-[#51C854] text-white hover:bg-[#46B649] active:scale-[0.98]' 
                      : 'bg-[#DEDEDE] text-[#999999] cursor-not-allowed'
                  }`}
                >
                  다음 단계
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 토스트 메시지 */}
      {toast.show && (
        <Toast
          message={toast.message}
          isVisible={toast.show}
          onClose={() => setToast({ show: false, message: '' })}
        />
      )}
    </DesktopLayout>
  );
}